/**
 * @cfleet/api-client - Customer Invitation Hooks
 *
 * React Query hooks for customer invitation operations.
 * These hooks support the invitation acceptance flow for customer app users.
 *
 * Endpoints:
 * - GET /customer/auth/invitation/{token} - Get invitation details (public)
 * - POST /customer/auth/accept-invite - Accept invitation and set password (public, new customers)
 * - POST /customer/auth/accept-org-invite - Accept org invite (authenticated, existing customers)
 *
 * @example
 * ```tsx
 * import {
 *   useCustomerInvitationDetails,
 *   useCustomerAcceptInvitation,
 *   useCustomerAcceptOrgInvitation,
 * } from '@cfleet/api-client';
 *
 * // Get invitation details
 * const { data: invitation } = useCustomerInvitationDetails(token);
 *
 * // Accept invitation (new customer)
 * const { mutate: acceptInvite } = useCustomerAcceptInvitation();
 * acceptInvite({ token, password: 'SecurePass123!' });
 *
 * // Accept org invitation (existing customer, authenticated)
 * const { mutate: acceptOrgInvite } = useCustomerAcceptOrgInvitation({ authConfig });
 * acceptOrgInvite({ token });
 * ```
 */

import { useMutation, useQuery } from '@tanstack/react-query';
import type { ClientConfig } from '../index';
import type { AuthMiddlewareConfig } from '../middleware';

// ─────────────────────────────────────────────────────────────────────────────
// Type Definitions
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Invitation details returned from GET /customer/auth/invitation/{token}
 */
export interface CustomerInvitationDetails {
  /** Email address the invitation was sent to */
  email: string;
  /** Name of the invited customer */
  name: string;
  /** Name of the organization that is inviting this customer */
  fleet_company_name: string;
  /** Name of the person who sent the invitation */
  invited_by_name: string;
  /** When this invitation expires (ISO 8601 date-time) */
  expires_at: string;
  /** True if the customer already has a password set (existing account) */
  is_existing_customer: boolean;
}

/**
 * Request body for accepting an invitation (new customers)
 */
export interface CustomerAcceptInviteRequest {
  /** 64-character invitation token from email */
  token: string;
  /** Password to set for the new account (8-128 characters) */
  password: string;
}

/**
 * Request body for accepting an org invitation (existing customers)
 */
export interface CustomerAcceptOrgInviteRequest {
  /** 64-character invitation token from email */
  token: string;
}

/**
 * Customer profile data
 */
export interface CustomerProfile {
  id: string;
  name: string;
  email: string;
  company?: string;
  phone?: string;
  avatar?: string | null;
  created_at?: string;
  last_login?: string;
}

/**
 * Response from accepting an invitation (includes JWT for auto-login)
 */
export interface CustomerLoginResponse {
  /** JWT access token */
  token: string;
  /** Refresh token for obtaining new access tokens */
  refresh_token?: string;
  /** Token expiry time in seconds */
  expires_in: number;
  /** Customer profile */
  customer: CustomerProfile;
}

/**
 * Organization summary after accepting org invite
 */
export interface CustomerOrganizationSummary {
  id: string;
  name: string;
  slug: string;
}

/**
 * Response from accepting an org invitation
 */
export interface CustomerAcceptOrgInviteResponse {
  /** Human-readable success message */
  message: string;
  /** Organization details */
  organization: CustomerOrganizationSummary;
}

/**
 * API error response (RFC 7807)
 */
export interface ApiErrorResponse {
  type: string;
  title: string;
  status: number;
  detail?: string;
  errors?: Array<{
    field: string;
    message: string;
    code?: string;
  }>;
}

// ─────────────────────────────────────────────────────────────────────────────
// Configuration
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Configuration for customer invitation hooks (public endpoints)
 */
export interface UseCustomerInvitationHooksConfig {
  /** Custom client configuration (optional) */
  clientConfig?: ClientConfig;
}

/**
 * Configuration for authenticated customer invitation hooks
 */
export interface UseCustomerInvitationAuthHooksConfig {
  /** Authentication middleware configuration (required) */
  authConfig: AuthMiddlewareConfig;
}

// ─────────────────────────────────────────────────────────────────────────────
// Custom Error Class
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Custom error class for invitation API errors
 * Includes HTTP status code for proper error handling
 */
export class InvitationError extends Error {
  public readonly status: number;
  public readonly detail: string;

  constructor(status: number, detail: string) {
    super(detail);
    this.name = 'InvitationError';
    this.status = status;
    this.detail = detail;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// useCustomerInvitationDetails
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook to get customer invitation details by token.
 *
 * This is a public endpoint (no auth required).
 * Used on the accept-invite page to display invitation information.
 *
 * @param token - Invitation token from email link (null/empty disables query)
 * @param config - Optional configuration including custom client config
 * @returns Query hook with invitation details
 *
 * @example
 * ```tsx
 * const token = searchParams.get('token');
 * const { data: invitation, isLoading, error } = useCustomerInvitationDetails(token);
 *
 * if (isLoading) return <LoadingSpinner />;
 *
 * if (error) {
 *   if (error.status === 404) return <InvalidInvitationPage />;
 *   if (error.status === 410) return <ExpiredInvitationPage />;
 *   return <ErrorPage message={error.message} />;
 * }
 *
 * return (
 *   <AcceptInviteForm
 *     email={invitation.email}
 *     name={invitation.name}
 *     companyName={invitation.fleet_company_name}
 *     invitedBy={invitation.invited_by_name}
 *     isExistingCustomer={invitation.is_existing_customer}
 *   />
 * );
 * ```
 */
export function useCustomerInvitationDetails(
  token: string | null | undefined,
  config?: UseCustomerInvitationHooksConfig
) {
  return useQuery<CustomerInvitationDetails, InvitationError>({
    queryKey: ['customer-invitation', 'token', token],
    queryFn: async (): Promise<CustomerInvitationDetails> => {
      if (!token) {
        throw new InvitationError(400, 'No token provided');
      }

      const response = await fetch(
        `${config?.clientConfig?.baseUrl || '/api/v1'}/customer/auth/invitation/${encodeURIComponent(token)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            ...config?.clientConfig?.headers,
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({})) as ApiErrorResponse;
        throw new InvitationError(
          response.status,
          errorData.detail || getDefaultErrorMessage(response.status)
        );
      }

      return response.json();
    },
    enabled: !!token,
    staleTime: 5 * 60 * 1000, // 5 minutes - invitation details don't change often
    retry: false, // Don't retry on 404/410 errors
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// useCustomerAcceptInvitation
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook to accept an invitation and create a password for NEW customers.
 *
 * This is a public endpoint (no auth required).
 * Returns JWT credentials for automatic login after successful acceptance.
 *
 * @param config - Optional configuration including custom client config
 * @returns Mutation hook for accepting invitation
 *
 * @example
 * ```tsx
 * const { mutate: acceptInvite, isPending, error } = useCustomerAcceptInvitation();
 *
 * const handleAccept = (password: string) => {
 *   acceptInvite({ token, password }, {
 *     onSuccess: (response) => {
 *       // Auto-login: store tokens and navigate to dashboard
 *       setTokens(response.token, response.refresh_token, response.expires_in);
 *       navigate('/dashboard');
 *     },
 *     onError: (error) => {
 *       if (error.status === 409) {
 *         // Customer already has password, redirect to login
 *         navigate('/login');
 *       } else {
 *         toast.error(error.message);
 *       }
 *     },
 *   });
 * };
 * ```
 */
export function useCustomerAcceptInvitation(config?: UseCustomerInvitationHooksConfig) {
  return useMutation<CustomerLoginResponse, InvitationError, CustomerAcceptInviteRequest>({
    mutationFn: async (data: CustomerAcceptInviteRequest): Promise<CustomerLoginResponse> => {
      const response = await fetch(
        `${config?.clientConfig?.baseUrl || '/api/v1'}/customer/auth/accept-invite`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...config?.clientConfig?.headers,
          },
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({})) as ApiErrorResponse;
        throw new InvitationError(
          response.status,
          errorData.detail || getDefaultErrorMessage(response.status)
        );
      }

      return response.json();
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// useCustomerAcceptOrgInvitation
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Hook to accept an organization invitation for EXISTING customers.
 *
 * Requires authentication. Adds the customer to the inviting organization.
 *
 * @param config - Configuration including auth config (required)
 * @returns Mutation hook for accepting org invitation
 *
 * @example
 * ```tsx
 * const authConfig = useAuthConfig();
 * const { mutate: acceptOrgInvite, isPending } = useCustomerAcceptOrgInvitation({ authConfig });
 *
 * const handleAcceptOrg = () => {
 *   acceptOrgInvite({ token }, {
 *     onSuccess: (response) => {
 *       toast.success(response.message);
 *       // Refresh organizations list or navigate to new org
 *       navigate(`/organizations/${response.organization.slug}`);
 *     },
 *     onError: (error) => {
 *       if (error.status === 409) {
 *         toast.info('You are already a member of this organization');
 *       } else {
 *         toast.error(error.message);
 *       }
 *     },
 *   });
 * };
 * ```
 */
export function useCustomerAcceptOrgInvitation(config: UseCustomerInvitationAuthHooksConfig) {
  return useMutation<CustomerAcceptOrgInviteResponse, InvitationError, CustomerAcceptOrgInviteRequest>({
    mutationFn: async (data: CustomerAcceptOrgInviteRequest): Promise<CustomerAcceptOrgInviteResponse> => {
      // Note: We can't use openapi-fetch here since the endpoint isn't in the generated types yet
      // Using fetch directly with the auth token from the middleware config
      const response = await fetch(
        `${config.authConfig.baseUrl}/customer/auth/accept-org-invite`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            // Auth token will be added by the auth context/middleware
          },
          credentials: 'include',
          body: JSON.stringify(data),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({})) as ApiErrorResponse;
        throw new InvitationError(
          response.status,
          errorData.detail || getDefaultErrorMessage(response.status)
        );
      }

      return response.json();
    },
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// Helper Functions
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Get default error message based on HTTP status code
 */
function getDefaultErrorMessage(status: number): string {
  switch (status) {
    case 400:
      return 'Invalid request';
    case 401:
      return 'Authentication required';
    case 404:
      return 'Invitation not found or has been cancelled';
    case 409:
      return 'This invitation has already been accepted';
    case 410:
      return 'This invitation has expired. Please contact the organization for a new invitation.';
    case 429:
      return 'Too many requests. Please try again later.';
    default:
      return 'An unexpected error occurred';
  }
}
